import {
  Box,
  Button,
  Flex,
  Icon,
  SearchInput,
  Space,
  Stack,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { AddIcon, CloseSIcon } from '@tonic-one/react-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DataGrid,
  DATA_GRID_CHECKBOX_SELECTION_COLUMN_DEF,
  ColumnDef,
} from '@tonic-one/react-data-grid';

import ErrorAlert from '@Components/Common/ErrorAlert';
import { useDisclosure } from '@Components/Common/Tooltip/TextEllipsis/utils';
import IconButton from '@Components/IconButton';
import { useOrgStore } from '@Models/useOrgModel';
import { DeleteApprovedUsersDto } from '@Types/globalSetting';
import { PolicyTypeEnum } from '@Policy/utils/constants';
import DelModal from '@Report/ActionModal/del';
import LoadingBtn from '@Components/Common/LoadingBtn';
import SmartTooltip from '@Report/SmartTooltip';
import AddSiteModal from './AddSiteModal';
import { useApprovedSitesQuery, useApprovedSitesMutations } from './hooks';

interface ApprovedSitesProps {
  companyGuid: string;
  policyType: number;
  name: string;
  email: string;
  siteName?: string;
  siteUrl: string;
}

const ROW_HEIGHT = 36;

const ApprovedSites = (props: {
  policyType: PolicyTypeEnum;
  withEditPermission: boolean;
  loading: boolean;
  setLoading: (loading: boolean) => void;
}) => {
  const [colorMode] = useColorMode();
  const { orgsMap } = useOrgStore();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation(['globalSetting', 'common']);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [addModalVisible, setAddModalVisible] = useState<boolean>(false);
  const [delModalContent, setDelModalContent] = useState<
    JSX.Element[] | undefined
  >(undefined);
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [searchKeyword, setSearchKeyword] = useState('');
  const [showError, setShowError] = useState<boolean>(false);

  // Use React Query for data fetching
  const {
    data: allItems = [],
    isLoading,
    error,
  } = useApprovedSitesQuery(props.policyType);
  const { deleteSitesMutation } = useApprovedSitesMutations(props.policyType);

  // Filter items based on search
  const items = useMemo(() => {
    if (!searchKeyword) return allItems;

    return allItems.filter((item) => {
      const searchTarget = item.email.toLowerCase();
      const siteName = item.name.toLowerCase();
      return (
        searchTarget.includes(searchKeyword.toLowerCase()) ||
        siteName.includes(searchKeyword.toLowerCase())
      );
    });
  }, [allItems, searchKeyword]);

  const search = useCallback((keyword: string) => {
    setSearchKeyword(keyword);
  }, []);

  const getRowId = useCallback((row: ApprovedSitesProps) => {
    return `${row.siteUrl}|${row.policyType}`;
  }, []);

  const handleRowSelectionChange = useCallback(
    (newRowSelection: Record<string, boolean>) => {
      setRowSelection(newRowSelection);
    },
    []
  );

  useEffect(() => {
    if (error) {
      setShowError(true);
    }
  }, [error]);

  const columns = useMemo(() => {
    const cellProps = {
      display: 'flex',
      alignItems: 'center',
      height: ROW_HEIGHT,
    };

    const checkboxColumnDef = {
      ...DATA_GRID_CHECKBOX_SELECTION_COLUMN_DEF,
      cellProps: {
        ...DATA_GRID_CHECKBOX_SELECTION_COLUMN_DEF.cellProps,
        display: 'flex',
        alignItems: 'center',
        height: ROW_HEIGHT,
      },
    };

    return [
      checkboxColumnDef,
      {
        id: 'policyType',
        header: (
          <SmartTooltip>
            {t('globalSetting.approvedUser.column.policyType')}
          </SmartTooltip>
        ),
        accessorKey: 'policyType',
        width: 'auto',
        minWidth: 200,
        cellProps,
        cell: (params: { row: { value: ApprovedSitesProps } }) => {
          const { policyType } = params.row.value;
          const type =
            policyType === 1
              ? t('globalSetting.approvedUser.table.policyType.atp')
              : policyType === 2
                ? t('globalSetting.approvedUser.table.policyType.dlp')
                : t('globalSetting.approvedUser.table.policyType.atp');
          return <SmartTooltip>{type}</SmartTooltip>;
        },
      },
      {
        id: 'siteUrl',
        header: (
          <SmartTooltip>
            {t('globalSetting.approvedSites.column.site')}
          </SmartTooltip>
        ),
        accessorKey: 'siteUrl',
        width: 'auto',
        minWidth: 200,
        cellProps,
        cell: ({ row }: { row: { value: ApprovedSitesProps } }) => {
          const { siteName } = row.value;
          return <SmartTooltip>{siteName}</SmartTooltip>;
        },
      },
      {
        id: 'companyGuid',
        header: (
          <SmartTooltip>
            {t('globalSetting.approvedUser.column.organization')}
          </SmartTooltip>
        ),
        accessorKey: 'companyGuid',
        width: 'auto',
        minWidth: 200,
        cellProps,
        cell: ({ row }: { row: { value: ApprovedSitesProps } }) => {
          const { companyGuid } = row.value;
          const orgName = orgsMap.get(companyGuid) || '';
          return <SmartTooltip>{orgName}</SmartTooltip>;
        },
      },
    ] satisfies ColumnDef[];
  }, [orgsMap, t]);

  const handleRemove = useCallback(async () => {
    const selectedIds = Object.keys(rowSelection).filter(
      (key) => rowSelection[key]
    );
    if (selectedIds.length === 0) {
      return;
    }

    const selectedRows = items.filter((item) =>
      selectedIds.includes(getRowId(item))
    );

    const content = selectedRows.map((item) => (
      <Box key={getRowId(item)} mb="2x">
        <Text>{item.siteName}</Text>
        <Text fontSize="xs" color={colorStyle.color.secondary}>
          {item.siteUrl}
        </Text>
      </Box>
    ));

    setDelModalContent(content);
    onOpen();
  }, [colorStyle.color.secondary, getRowId, items, onOpen, rowSelection]);

  const reset = useCallback(() => {
    setRowSelection({});
    setSearchKeyword('');
  }, []);

  const confirmDelete = useCallback(async () => {
    const selectedIds = Object.keys(rowSelection).filter(
      (key) => rowSelection[key]
    );
    const selectedRows = items.filter((item) =>
      selectedIds.includes(getRowId(item))
    );

    const users: DeleteApprovedUsersDto[] = selectedRows.map((item) => ({
      companyGuid: item.companyGuid,
      policyType: item.policyType,
      name: item.name,
      email: item.email,
    }));

    await deleteSitesMutation.mutateAsync(users);
    reset();
    onClose();
  }, [deleteSitesMutation, getRowId, items, onClose, reset, rowSelection]);

  const selectedCount = useMemo(
    () => Object.values(rowSelection).filter(Boolean).length,
    [rowSelection]
  );

  return (
    <Stack height="100%">
      {showError && (
        <ErrorAlert
          errorMsg={t('common.apiException.internalError')}
          onClose={() => setShowError(false)}
        />
      )}
      <Text fontSize="lg" lineHeight="lg" my="4x" px="4x">
        {t('globalSetting.approvedSites.title')}
      </Text>
      <Stack gap="4x" flex={1}>
        <Stack gap="4x" px="4x">
          <Text fontSize="sm" color={colorStyle.color.secondary}>
            {t('globalSetting.approvedSites.description')}
          </Text>

          <Text>
            <Space mr="1x">{t('globalSetting.note.title')}</Space>
            <Space color={colorStyle.color.secondary}>
              {t('globalSetting.note.applyToAllOrganizations')}
            </Space>
          </Text>

          <Flex alignItems="center">
            {selectedCount !== 0 ? (
              <Box mr={16}>
                <Stack direction="row">
                  <LoadingBtn
                    variant="secondary"
                    mr="4x"
                    onClick={handleRemove}
                    disabled={!props.withEditPermission}
                  >
                    {t('reportCommon.buttons.delete')}
                  </LoadingBtn>
                  <IconButton onClick={reset}>
                    <Icon as={CloseSIcon}></Icon>
                  </IconButton>
                  <Box lineHeight="2rem">
                    <Text color={colorStyle.color.tertiary}>
                      {selectedCount}&nbsp;
                      {t('common.label.selected')}
                    </Text>
                  </Box>
                </Stack>
              </Box>
            ) : null}
            {selectedCount === 0 ? (
              <Stack direction="row">
                <Button
                  variant="primary"
                  onClick={() => setAddModalVisible(true)}
                  disabled={!props.withEditPermission}
                >
                  <Icon as={AddIcon} mr={8} />
                  {t('globalSetting.approvedUser.action.label.add')}
                </Button>
                <Space width="12px" />
                <SearchInput
                  placeholder={t('globalSetting.approvedSites.column.site')}
                  onChange={(e) => search(e.target.value)}
                  onClearInput={() => search('')}
                  width="374px"
                  value={searchKeyword}
                />
              </Stack>
            ) : null}
          </Flex>
        </Stack>

        <Flex minHeight={300} flex={1}>
          <DataGrid
            columns={columns}
            data={items}
            getRowId={getRowId}
            isColumnSortable={false}
            rowSelection={rowSelection}
            onRowSelectionChange={handleRowSelectionChange}
            emptyContent={t('common.noData')}
            isLoading={isLoading}
            autoPagination
            showPagination
          />
        </Flex>
      </Stack>

      {addModalVisible && (
        <AddSiteModal
          isOpen={addModalVisible}
          onClose={() => setAddModalVisible(false)}
          policyType={props.policyType}
          currentSitesCount={items.length}
        />
      )}

      <DelModal
        visible={isOpen}
        onClose={onClose}
        title={t('globalSetting.approvedSites.delete.title')}
        loading={deleteSitesMutation.isPending}
        onConfirm={confirmDelete}
        withEditPermission={props.withEditPermission}
      >
        {delModalContent}
      </DelModal>
    </Stack>
  );
};

export default ApprovedSites;
